import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './inboxIcons';
import { themeStyle, DEFAULT_ACCENT } from './inboxTheme';
import Inbox from './Inbox';
import AgentPage from './AgentPage';
import CustomersPage from './CustomersPage';
import AnalyticsPage from './AnalyticsPage';
import SettingsPage from './SettingsPage';
import WhatsAppPage from './WhatsAppPage';
import MyReportsPage from './MyReportsPage';
import CustomerPhotosPage from './CustomerPhotosPage';
import ProblemReport from './ProblemReport';
import './inbox.css';

// The support workspace: icon rail on the left, one page at a time on the right.
// Every page is gated by the same permissions as the old tabs.
function InboxWorkspace({ apiBase, getToken, me, perms, selectRequest, onSelectHandled, refreshKey, onNewTicket, onArchive }) {
  const rootRef = useRef(null);
  const [height, setHeight] = useState(640);
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [openRequest, setOpenRequest] = useState(null);
  const [unread, setUnread] = useState(0);
  const [reportNews, setReportNews] = useState(0);

  const pages = [
    perms.inbox && { id: 'inbox', label: 'Inbox', icon: 'inbox' },
    perms.agent && { id: 'agent', label: 'AI agent', icon: 'spark' },
    perms.customers && { id: 'customers', label: 'Customers', icon: 'users' },
    perms.metrics && { id: 'reports', label: 'Analytics', icon: 'chart' },
    perms.inbox && { id: 'photos', label: 'Customer photos', icon: 'image' },
    (perms.admin || perms.manager) && { id: 'whatsapp', label: 'WhatsApp', icon: 'whatsapp' }, // managers: chat flows only
    (perms.admin || perms.manager) && { id: 'settings', label: 'Settings', icon: 'sliders' },
    { id: 'myreports', label: 'Your reports', icon: 'flag' },
  ].filter(Boolean);

  const [page, setPage] = useState(() => {
    // The Monday WhatsApp summary links to /support?weekly=<week>: open that report
    try { if (new URLSearchParams(window.location.search).get('weekly')) return 'reports'; } catch { /* no URL */ }
    try { return localStorage.getItem('cx-page') || 'inbox'; } catch { return 'inbox'; }
  });
  const current = pages.find((p) => p.id === page) ? page : pages[0]?.id;
  const go = useCallback((id) => {
    setPage(id);
    try { localStorage.setItem('cx-page', id); } catch { /* storage unavailable */ }
  }, []);

  const api = useCallback(async (path, method = 'GET', body) => {
    const token = await getToken();
    const res = await fetch(`${apiBase}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.message || `Request failed (${res.status})`);
    return data;
  }, [apiBase, getToken]);

  // Fill the screen below whatever sits above the workspace
  useEffect(() => {
    const fit = () => {
      const top = rootRef.current?.getBoundingClientRect().top || 0;
      setHeight(Math.max(560, window.innerHeight - top - 16));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  useEffect(() => {
    api('/api/v1/support/appearance').then((d) => setAccent(d.accent || DEFAULT_ACCENT)).catch(() => {});
    const onAccent = (e) => setAccent(e.detail || DEFAULT_ACCENT);
    window.addEventListener('cx-accent', onAccent);
    return () => window.removeEventListener('cx-accent', onAccent);
  }, [api]);

  // Opened from outside (notifications) or from another page here
  useEffect(() => {
    if (!selectRequest) return;
    setOpenRequest(selectRequest);
    go('inbox');
    onSelectHandled?.();
  }, [selectRequest, onSelectHandled, go]);

  // New conversations badge on the Inbox icon. The inbox reports it while open; elsewhere we check every minute.
  useEffect(() => {
    if (current === 'inbox' || !(perms.tickets || perms.chats)) return undefined;
    const check = () => api('/api/v1/support/inbox')
      .then((d) => setUnread((d.items || []).filter((i) => i.status === 'new' && !i.snoozedUntilMs).length))
      .catch(() => {});
    check();
    const t = setInterval(() => { if (!document.hidden) check(); }, 60000);
    return () => clearInterval(t);
  }, [api, current, perms.tickets, perms.chats]);

  // Replies to your problem reports: badge on the Your reports icon (checked every 2 minutes)
  const reportsSeen = useCallback(() => setReportNews(0), []);
  const checkReports = useCallback(() => { api('/api/v1/support/problems/mine').then((d) => setReportNews(d.unseen || 0)).catch(() => {}); }, [api]);
  useEffect(() => {
    checkReports();
    const t = setInterval(() => { if (!document.hidden) checkReports(); }, 120000);
    return () => clearInterval(t);
  }, [checkReports]);

  const openTicket = useCallback((ticketId) => {
    if (!perms.inbox) return;
    setOpenRequest(ticketId);
    go('inbox');
  }, [perms.inbox, go]);

  return (
    <div ref={rootRef} className="cx-app" style={{ ...themeStyle(accent), height }}>
      <nav className="cx-rail" aria-label="Support workspace">
        {pages.map((p) => (
          <button key={p.id} className="cx-rail-btn" aria-current={current === p.id ? 'page' : undefined} onClick={() => go(p.id)} aria-label={p.label}>
            <Icon name={p.icon} />
            {p.id === 'inbox' && unread > 0 && <span className="cx-rail-count" aria-label={`${unread} new`}>{unread > 99 ? '99+' : unread}</span>}
            {p.id === 'myreports' && reportNews > 0 && <span className="cx-rail-count" aria-label={`${reportNews} new replies`}>{reportNews}</span>}
            <span className="cx-rail-tip">{p.label}{p.id === 'inbox' && unread > 0 ? ` · ${unread} new` : ''}</span>
            <span className="cx-rail-label">{p.label}</span>
          </button>
        ))}
        <span className="cx-rail-spacer" />
      </nav>

      {current === 'inbox' && (
        <Inbox
          apiBase={apiBase}
          getToken={getToken}
          me={me}
          perms={perms}
          accent={accent}
          fill
          refreshKey={refreshKey}
          selectRequest={openRequest}
          onSelectHandled={() => setOpenRequest(null)}
          onNewTicket={onNewTicket}
          onArchive={onArchive}
          onUnreadChange={setUnread}
        />
      )}
      {current === 'agent' && <AgentPage api={api} isAdmin={perms.admin} onOpenTicket={perms.inbox ? openTicket : null} />}
      {current === 'customers' && <CustomersPage api={api} canOpen={perms.inbox} onOpenTicket={openTicket} />}
      {current === 'reports' && <AnalyticsPage api={api} onGo={(id) => pages.some((p) => p.id === id) && go(id)} />}
      {current === 'whatsapp' && <WhatsAppPage api={api} isAdmin={!!perms.admin} />}
      {current === 'photos' && <CustomerPhotosPage api={api} apiBase={apiBase} getToken={getToken} />}
      {current === 'myreports' && <MyReportsPage api={api} onSeen={reportsSeen} onOpenTicket={perms.inbox ? openTicket : null} />}
      {current !== 'inbox' && <ProblemReport api={api} toast={() => {}} ticketId={null} channel={null} />}
      {current === 'settings' && <SettingsPage api={api} apiBase={apiBase} getToken={getToken} isAdmin={!!perms.admin} />}
      {!current && <div className="cx-page"><div className="cx-empty"><strong>No support pages</strong>Ask an admin for access to the inbox.</div></div>}
    </div>
  );
}

export default InboxWorkspace;
